import { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { api, queryString } from "@/lib/api";
import { useAuth } from "@/auth/auth";
import { useEntityAction } from "@/lib/mutations";
import { formatMinor } from "@/lib/hooks";
import { Button, Card, ErrorNote, Modal, Spinner, StatusBadge } from "@/components/ui";
import { ConfirmButton } from "@/components/ConfirmButton";
import { PayoutState } from "@/components/PayoutState";
import { DateField, Labeled, Select } from "@/components/Form";
import { formatCompanyDateTime, formatDateOnly } from "@/lib/time";
import type {
  AgentBookingRow,
  AgentRow,
  AgentsReport,
  TravelAgent,
} from "@/lib/types";

const CCY = "AED";
const money = (minor: number) => formatMinor(minor, CCY);

/** Everything the page reads from or refreshes after a payout. */
const AGENT_KEYS = [["agents"], ["home"], ["finance"], ["booking"]];

/** «Отметить выплату» — asks first (rule 8's pattern): who, which booking, how
 * much — Да / Отмена. The server pays what is owed on the booking right now. */
function PayButton({ agent, row }: { agent: AgentRow; row: AgentBookingRow }) {
  const { t } = useTranslation();
  const [asking, setAsking] = useState(false);
  const pay = useEntityAction(AGENT_KEYS, {
    inlineError: true,
    successMessage: t("agentsPage.paidToast"),
    onSuccess: () => setAsking(false),
  });
  const close = () => {
    pay.reset();
    setAsking(false);
  };
  return (
    <>
      <Button className="px-3 py-1 text-xs" onClick={() => setAsking(true)}>
        {t("agentsPage.markPaid")}
      </Button>
      {asking && (
        <Modal title={t("agentsPage.payAsk")} size="sm" busy={pay.isPending} onClose={close}>
          <p className="text-sm text-sand">
            {agent.name} · {row.booking_number}
            {row.customer_name ? ` · ${row.customer_name}` : ""} —{" "}
            <strong>{money(row.owed_minor)}</strong>
          </p>
          {pay.isError && (
            <div className="mt-2">
              <ErrorNote message={(pay.error as Error).message} />
            </div>
          )}
          <div className="mt-4 flex gap-2">
            <Button
              loading={pay.isPending}
              onClick={() =>
                pay.mutate(() => api.post(`/agents/commissions/${row.commission_id}/pay`))
              }
            >
              {t("agentsPage.payYes")}
            </Button>
            <button
              type="button"
              className="text-sm text-sand-muted hover:text-sand disabled:opacity-50"
              disabled={pay.isPending}
              onClick={close}
            >
              {t("common.cancel")}
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}

/** Paid commissions are not a debt — they stay visible here as history, and a
 * mis-tapped one is undone (the commission is owed again). */
function PayoutHistory({ row, canPay }: { row: AgentBookingRow; canPay: boolean }) {
  const { t } = useTranslation();
  const undo = useEntityAction(AGENT_KEYS, { successMessage: t("agentsPage.voidedToast") });
  if (row.payouts.length === 0) return null;
  return (
    <ul className="mt-1 space-y-0.5 text-xs text-sand-muted">
      {row.payouts.map((p) => (
        <li key={p.id} className="flex flex-wrap items-center gap-2">
          <span>
            {t("agentsPage.paidOn", {
              amount: money(p.amount_minor),
              date: formatCompanyDateTime(p.paid_at),
            })}
            {p.paid_by ? ` · ${p.paid_by}` : ""}
          </span>
          {canPay && (
            <ConfirmButton
              label={t("agentsPage.undoPayout")}
              confirmLabel={t("common.confirm")}
              busy={undo.isPending}
              onConfirm={() => undo.mutate(() => api.post(`/agents/payouts/${p.id}/void`))}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/** The agent's bookings: tourist, phone, trip date, party, booking number,
 * commission, payout status and the pay button. */
function AgentBookings({ agent, canPay }: { agent: AgentRow; canPay: boolean }) {
  const { t } = useTranslation();
  const terms = (row: AgentBookingRow) =>
    row.commission_type === "percent"
      ? t("bookingAgent.percentOfSale", { percent: row.commission_percent })
      : row.commission_type === "none"
        ? t("bookingAgent.none")
        : t("bookingAgent.fixedShort");
  if (agent.bookings.length === 0) {
    return <p className="px-4 py-3 text-sm text-sand-muted">{t("agentsPage.noBookings")}</p>;
  }
  return (
    <div className="overflow-x-auto">
      <table className="nm-table w-full min-w-[56rem] table-fixed text-sm">
        <colgroup>
          <col className="w-[15%]" />
          <col className="w-[12%]" />
          <col className="w-[10%]" />
          <col className="w-[7%]" />
          <col className="w-[11%]" />
          <col className="w-[13%]" />
          <col className="w-[17%]" />
          <col className="w-[15%]" />
        </colgroup>
        <thead className="text-left text-xs text-sand-muted">
          <tr>
            <th className="px-3 py-2 font-normal">{t("agentsPage.tourist")}</th>
            <th className="px-3 py-2 font-normal">{t("fields.phone")}</th>
            <th className="px-3 py-2 font-normal">{t("agentsPage.tripDate")}</th>
            <th className="px-3 py-2 text-right font-normal">{t("agentsPage.tourists")}</th>
            <th className="px-3 py-2 font-normal">{t("fields.booking")}</th>
            <th className="px-3 py-2 text-right font-normal">{t("agentsPage.commission")}</th>
            <th className="px-3 py-2 font-normal">{t("agentsPage.payoutStatus")}</th>
            <th className="px-3 py-2" />
          </tr>
        </thead>
        <tbody>
          {agent.bookings.map((row) => (
            <tr key={row.commission_id} className="border-t border-olive-700 align-top">
              <td className="px-3 py-2">
                {row.customer_name ?? (
                  <span className="text-amber-700">{t("bookings.noCustomer")}</span>
                )}
              </td>
              <td className="px-3 py-2 text-sand-muted">{row.customer_phone ?? "—"}</td>
              <td className="px-3 py-2">{row.trip_date ? formatDateOnly(row.trip_date) : "—"}</td>
              <td className="px-3 py-2 text-right">{row.pax}</td>
              <td className="px-3 py-2">
                <Link
                  className="whitespace-nowrap text-sand hover:text-gold"
                  to={`/bookings/${row.booking_number}`}
                >
                  {row.booking_number}
                </Link>
                {row.state === "void" && (
                  <div className="mt-0.5">
                    <StatusBadge status={row.booking_status} />
                  </div>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                {row.state === "void" ? (
                  "—"
                ) : (
                  <>
                    {money(row.commission_minor)}
                    <div className="text-xs text-sand-muted">{terms(row)}</div>
                  </>
                )}
              </td>
              <td className="px-3 py-2">
                <PayoutState state={row.state} />
                {/* the amount only when it is not just the commission again (rule 5) */}
                {row.state === "due" && row.owed_minor !== row.commission_minor && (
                  <div className="mt-0.5 text-xs text-gold">{money(row.owed_minor)}</div>
                )}
                {(row.state === "expected" || row.state === "partial") &&
                  row.expected_minor > 0 &&
                  row.expected_minor !== row.commission_minor && (
                    <div className="mt-0.5 text-xs text-sand-muted">{money(row.expected_minor)}</div>
                  )}
                <PayoutHistory row={row} canPay={canPay} />
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right">
                {canPay && row.owed_minor > 0 && <PayButton agent={agent} row={row} />}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function AgentsPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const canPay = can("agents:pay_commission");
  const [agentId, setAgentId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [onlyUnpaid, setOnlyUnpaid] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());

  const agentsList = useQuery({
    queryKey: ["travel-agents"],
    queryFn: () => api.get<TravelAgent[]>("/travel-agents"),
    enabled: can("bookings:read"),
  });
  const report = useQuery({
    queryKey: ["agents", agentId, dateFrom, dateTo, onlyUnpaid],
    queryFn: () =>
      api.get<AgentsReport>(
        `/agents${queryString({
          agent_id: agentId || undefined,
          date_from: dateFrom || undefined,
          date_to: dateTo || undefined,
          only_unpaid: onlyUnpaid ? "true" : undefined,
        })}`,
      ),
  });

  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const filtersOn = !!(agentId || dateFrom || dateTo || onlyUnpaid);
  const reset = () => {
    setAgentId("");
    setDateFrom("");
    setDateTo("");
    setOnlyUnpaid(false);
  };

  const data = report.data;
  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">{t("agents.title")}</h1>

      <Card>
        <div className="text-xs text-sand-muted">
          {t("agentsPage.totalOwed")}
        </div>
        <div className="mt-1 text-2xl font-semibold text-gold">
          {data ? money(data.total_owed_minor) : "…"}
        </div>
        {data && filtersOn && (
          <div className="mt-1 text-xs text-sand-muted">
            {t("agentsPage.owedByFilter", { amount: money(data.filtered_owed_minor) })}
          </div>
        )}
        <p className="mt-2 text-xs text-sand-muted">{t("agentsPage.hint")}</p>
      </Card>

      <Card>
        <div className="grid items-end gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <Labeled label={t("agentsPage.agent")}>
            <Select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">{t("common.all")}</option>
              {(agentsList.data ?? []).map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          </Labeled>
          <Labeled label={t("agentsPage.tripFrom")}>
            <DateField value={dateFrom} onChange={setDateFrom} />
          </Labeled>
          <Labeled label={t("agentsPage.tripTo")}>
            <DateField value={dateTo} min={dateFrom || undefined} onChange={setDateTo} />
          </Labeled>
          <label className="flex items-center gap-2 pb-2 text-sm text-sand">
            <input
              type="checkbox"
              checked={onlyUnpaid}
              onChange={(e) => setOnlyUnpaid(e.target.checked)}
            />
            {t("agentsPage.onlyUnpaid")}
          </label>
          {filtersOn && (
            <button
              type="button"
              className="pb-2 text-left text-sm text-sand-muted hover:text-gold"
              onClick={reset}
            >
              {t("common.reset")}
            </button>
          )}
        </div>
      </Card>

      {report.isLoading && <Spinner />}
      {report.isError && <ErrorNote message={(report.error as Error).message} />}
      {data && data.agents.length === 0 && (
        <p className="text-sm text-sand-muted">
          {filtersOn ? t("agentsPage.noMatch") : t("agentsPage.empty")}
        </p>
      )}

      {data && data.agents.length > 0 && (
        <div className="overflow-x-auto nm-card">
          <table className="nm-table w-full min-w-[56rem] table-fixed text-sm">
            <colgroup>
              <col className="w-[26%]" />
              <col className="w-[15%]" />
              <col className="w-[8%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
              <col className="w-[13%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead className="bg-olive-700 text-left text-xs text-sand">
              <tr>
                <th className="px-3 py-2 font-medium">{t("agentsPage.agent")}</th>
                <th className="px-3 py-2 font-medium">{t("fields.phone")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("agentsPage.tourists")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("agentsPage.revenue")}</th>
                <th className="px-3 py-2 text-right font-medium">{t("agentsPage.profit")}</th>
                <th className="px-3 py-2 text-right font-medium">
                  {t("agentsPage.expectedProfit")}
                </th>
                <th className="px-3 py-2 text-right font-medium">{t("agentsPage.owedNow")}</th>
              </tr>
            </thead>
            <tbody>
              {data.agents.map((agent) => {
                const expanded = open.has(agent.id);
                return (
                  <Fragment key={agent.id}>
                    <tr
                      className="cursor-pointer border-t border-olive-700 hover:bg-olive-700/40"
                      onClick={() => toggle(agent.id)}
                    >
                      <td className="px-3 py-2 font-medium">
                        <button
                          type="button"
                          className="mr-2 inline-block w-4 text-sand-muted"
                          aria-expanded={expanded}
                          aria-label={agent.name}
                        >
                          {expanded ? "▾" : "▸"}
                        </button>
                        {agent.name}
                        <span className="ml-2 whitespace-nowrap text-xs font-normal text-sand-muted">
                          {t("agentsPage.bookingsCount", { count: agent.bookings_count })}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-3 py-2 text-sand-muted">{agent.phone ?? "—"}</td>
                      <td className="px-3 py-2 text-right">{agent.tourists}</td>
                      <td className="px-3 py-2 text-right">{money(agent.revenue_minor)}</td>
                      <td className="px-3 py-2 text-right">{money(agent.profit_minor)}</td>
                      <td className="px-3 py-2 text-right text-sand-muted">
                        {money(agent.expected_profit_minor)}
                      </td>
                      <td
                        className={
                          "px-3 py-2 text-right font-semibold " +
                          (agent.owed_minor > 0 ? "text-gold" : "text-sand-muted")
                        }
                      >
                        {money(agent.owed_minor)}
                      </td>
                    </tr>
                    {expanded && (
                      <tr className="border-t border-olive-700 bg-olive-900/40">
                        <td colSpan={7} className="p-0">
                          <AgentBookings agent={agent} canPay={canPay} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
